import { useCallback, useEffect, useRef, useState } from 'react';
import { useSWRConfig } from 'swr';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components';
import { getEventEntitiesCacheKey } from '@/constants/cache-keys';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine/engine-service';
import { revalidateEntityViews } from '@/utils/swr';
import type { EntityKind } from '@/utils/detection-classes';
import type { EventEntities } from '@/types/event';

interface UseEventEntityLinkOptions {
    eventId: string;
    entities?: EventEntities;
    entityKind: EntityKind | null;
}

export async function linkEntityToEvent(
    eventId: string,
    entityKind: EntityKind,
    targetEntityId: string,
): Promise<void> {
    if (entityKind === 'person') {
        await EngineService.attachPersonToEvent(eventId, {
            targetPersonId: targetEntityId,
        });
    } else {
        await EngineService.attachVehicleToEvent(eventId, {
            targetVehicleId: targetEntityId,
        });
    }
    await revalidateEntityViews(entityKind);
}

export async function unlinkEntityFromEvent(
    eventId: string,
    entityKind: EntityKind,
): Promise<void> {
    if (entityKind === 'person') {
        await EngineService.detachPersonFromEvent(eventId);
    } else {
        await EngineService.detachVehicleFromEvent(eventId);
    }
    await revalidateEntityViews(entityKind);
}

export function useEventEntityLink({
    eventId,
    entities,
    entityKind,
}: UseEventEntityLinkOptions) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const { mutate } = useSWRConfig();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isMountedRef = useRef(true);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const setSubmittingIfMounted = useCallback((value: boolean) => {
        if (isMountedRef.current) {
            setIsSubmitting(value);
        }
    }, []);

    const invalidate = useCallback(() => {
        void mutate(getEventEntitiesCacheKey(eventId));
    }, [mutate, eventId]);

    const link = useCallback(
        async (targetEntityId: string) => {
            if (!entityKind) return;
            setSubmittingIfMounted(true);
            try {
                await linkEntityToEvent(eventId, entityKind, targetEntityId);
                toast.success(t('eventView.linkEntity.success'));
                invalidate();
            } catch {
                toast.error(t('eventView.linkEntity.errorLink'));
            } finally {
                setSubmittingIfMounted(false);
            }
        },
        [entityKind, eventId, invalidate, setSubmittingIfMounted, t],
    );

    const unlink = useCallback(async () => {
        if (!entityKind) return;
        const previousEntityId =
            entityKind === 'person'
                ? entities?.persons?.[0]?.person.id
                : entities?.vehicles?.[0]?.vehicle.id;
        setSubmittingIfMounted(true);
        try {
            await unlinkEntityFromEvent(eventId, entityKind);
            invalidate();
            toast.success(t('eventView.linkEntity.successUnlink'), {
                duration: 8000,
                ...(previousEntityId && {
                    action: {
                        label: t('eventView.linkEntity.undo'),
                        onClick: () => void link(previousEntityId),
                    },
                }),
            });
        } catch {
            toast.error(t('eventView.linkEntity.errorUnlink'));
        } finally {
            setSubmittingIfMounted(false);
        }
    }, [
        entityKind,
        eventId,
        entities,
        invalidate,
        link,
        setSubmittingIfMounted,
        t,
    ]);

    const isLinked =
        entityKind === 'person'
            ? Boolean(entities?.persons?.length)
            : entityKind === 'vehicle'
              ? Boolean(entities?.vehicles?.length)
              : false;

    return { link, unlink, isSubmitting, isLinked };
}
